import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import GalleryPage from './GalleryPage';
import DatabasePage from './DatabasePage';
import ArticlesPage from './ArticlesPage';
import SoundtestsPage from './SoundtestsPage';
import InProgress from './InProgress';
import ArticleRouter from './components/ArticleRouter';
import RequestsPage from './RequestsPage';
import SwitchCatalogPage from './SwitchCatalogPage';
import galleryImages, { galleryList } from './galleryData';
import { builds, articles} from './dataStore';
import ShortFooter from './components/ShortFooter';
import { AudioProvider } from './components/AudioProvider';


// Pick a random gallery image for the Gallery card, only from visible items
const visibleGalleryKeys = galleryList;
const randomGalleryKey = visibleGalleryKeys[Math.floor(Math.random() * visibleGalleryKeys.length)];
const randomGalleryImage = galleryImages[randomGalleryKey] || '/pictures/home_bg.JPG';

console.log('Random Gallery Key:', randomGalleryKey);

// Pick a random soundtest build with hasSoundtest for the Soundtests card
const soundtestBuildsList = Object.values(builds).filter((b) => b.hasSoundtest);
const randomSoundtestBuild = soundtestBuildsList[Math.floor(Math.random() * soundtestBuildsList.length)];
const randomSoundtestImage = randomSoundtestBuild ? (
	randomSoundtestBuild.thumbnail || galleryImages[randomSoundtestBuild.keyboardId] || '/pictures/home_bg.JPG'
) : '/pictures/home_bg.JPG';

// Filter visible articles
const visibleArticles = articles.filter((article) => article.visible);
const randomArticle = visibleArticles[Math.floor(Math.random() * visibleArticles.length)];
const randomArticleImage = randomArticle ? randomArticle.thumbnail : '/pictures/home_bg.JPG';

const featured = [
	{
		title: 'Soundtests',
		desc: 'Listen to soundtests of our most popular mechanical switches.',
		image: randomSoundtestImage,
		link: '/soundtests',
	},
	{
		title: 'Articles',
		desc: 'Discover in-depth reviews, personal insights, and helpful guides.',
		image: randomArticleImage,
		link: '/articles',
	},
	{
		title: 'Gallery',
		desc: "Browse a showcase of custom keyboards we've built.",
		image: randomGalleryImage,
		link: '/gallery',
	}
];

function Navbar() {
	const [menuOpen, setMenuOpen] = React.useState(false);
	const [shouldRender, setShouldRender] = React.useState(false);
	const [visible, setVisible] = React.useState(false);
	const justOpenedRef = React.useRef(false); // Prevents immediate close after open
		const navLinks = [
			{ href: '/gallery', label: 'Gallery' },
			{ href: '/articles', label: 'Articles' },
			{ href: '/soundtests', label: 'Soundtests' },
			{ href: '/database', label: 'Database' },
			{ href: '/switch-catalog', label: 'Switch Catalog' },
			{ href: '/requests', label: 'Requests' },
		];

	React.useEffect(() => {
		if(menuOpen) {
			setShouldRender(true);
			const timeout = setTimeout(() => setVisible(true), 10);
			return () => clearTimeout(timeout);
		} else {
			setVisible(false);
			const timeout = setTimeout(() => setShouldRender(false), 300);
			return () => clearTimeout(timeout);
		}
	}, [menuOpen]);

	React.useEffect(() => {
		if(!menuOpen) return;
		const close = (e) => {
			if(!e.target.closest('#mobile-nav') && !e.target.closest('#hamburger-btn')) setMenuOpen(false);
		};
		document.addEventListener('mousedown', close);
		return () => document.removeEventListener('mousedown', close);
	}, [menuOpen]);

	// Debounce overlay close after hamburger click
	const handleHamburgerPointerDown = (e) => {
		e.stopPropagation();
		justOpenedRef.current = true;
		setMenuOpen((v) => !v);
		setTimeout(() => { justOpenedRef.current = false; }, 220); // 220ms lockout
	};

	const handleOverlayPointerUp = (e) => {
		if(justOpenedRef.current) return; // Ignore if just opened
		setMenuOpen(false);
	};

	return (
		<nav className='fixed top-0 left-0 w-full z-50 bg-transparent shadow-none mt-[5px]'>
			<div className='mx-auto flex items-center justify-between px-6 py-3'>
				<div className='flex items-center gap-2'>
					<a href='/'
						className='relative text-white font-bold text-lg tracking-widest ml-[10px] after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-white after:transition-all after:duration-300 hover:after:w-full transition px-0'
						style={{textShadow: '0.5px 0.5px 2px rgba(0,0,0,0.5)'}}
					>
						<span className='relative z-10'>AEYKBDS</span>
					</a>
				</div>
				{/* Desktop nav links */}
				<div className='hidden md:flex gap-6 font-semibold text-base mr-[10px]'>
					{navLinks.map((item) => (
						<a
							key={item.label}
							href={item.href}
							className='relative text-white after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-white after:transition-all after:duration-300 hover:after:w-full transition px-0'
							style={{ textShadow: '0.5px 0.5px 2px rgba(0,0,0,0.5)' }}
						>
							<span className='relative z-10'>{item.label}</span>
						</a>
					))}
					<a href='https://www.youtube.com/@aeykbds' target='_blank' rel='noopener noreferrer'
						className='relative text-white after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-red-500 after:transition-all after:duration-300 hover:after:w-full hover:text-red-500 transition px-0'
						style={{ textShadow: '0.5px 0.5px 2px rgba(0,0,0,0.5)' }}
					>
						<span className='relative z-10'>YouTube</span>
					</a>
				</div>
				{/* Hamburger for mobile */}
				<div className='flex md:hidden z-[100]'>
					<button
						id='hamburger-btn'
						className='block text-white text-3xl focus:outline-none px-2 py-1 rounded transition mt-[-1vh] pointer-events-auto relative z-[100]'
						onPointerDown={handleHamburgerPointerDown}
						aria-label='Open navigation menu'
					>
						&#9776;
					</button>
				</div>
			</div>
			{/* Mobile nav dropdown/sidebar */}
			{shouldRender && (
				<div id='mobile-nav' className={`fixed top-0 left-0 w-full h-full z-40 flex transition-all duration-300 ${visible ? 'backdrop-blur-sm opacity-100' : 'opacity-0 pointer-events-none'}`}
					onPointerUp={handleOverlayPointerUp}
				>
					<div className={`w-1/2 max-w-xs bg-black/70 border-r border-gray-800 shadow-xl h-full flex flex-col gap-6 p-8 rounded-r-[8px] relative transform transition-transform duration-300 ${visible ? 'translate-x-0' : 'translate-x-[-100%]'}`}
						onPointerUp={(e) => { e.stopPropagation(); }}
					>
						{/* Close button aligned with Gallery link, larger size */}
						<button
							className='absolute right-4 top-8 text-gray-100 text-4xl px-5 mr-[-4vw] mt-[1px] opacity-80 z-10'
							onPointerUp={() => setMenuOpen(false)}
							aria-label='Close menu'
						>
							&times;
						</button>
						{/* Navigation links start below, no longer pushed down by close button */}
						<div className='flex flex-col gap-6 mt-0'>
							{navLinks.map((item) => (
								<a
									key={item.label}
									href={item.href}
									className='text-gray-100/80 text-lg font-semibold py-2 px-2 rounded px-0'
									style={{ textShadow: '0.5px 0.5px 2px rgba(0,0,0,0.5)' }}
								>
									<span className='relative after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-white after:transition-all after:duration-300 hover:after:w-full transition'>{item.label}</span>
								</a>
							))}
							<a href='https://www.youtube.com/@aeykbds' target='_blank' rel='noopener noreferrer'
								className='text-gray-100/80 text-lg font-semibold py-2 px-2 rounded px-0'
								style={{ textShadow: '0.5px 0.5px 2px rgba(0,0,0,0.5)' }}
							>
								<span className='relative after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-red-500 after:transition-all after:duration-300 hover:after:w-full hover:text-red-500 transition'>YouTube</span>
							</a>
						</div>
					</div>
				</div>
			)}
		</nav>
	);
}

function FeaturedGrid() {
	return (
		<section className='w-full px-0 pb-8 mt-12'>
			<div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 w-full max-w-7xl mx-auto justify-center place-items-center'>
				{featured.map((item) => {
					let cta = 'Explore →';
					if(item.title === 'Soundtests') cta = 'Listen →';
					else if(item.title === 'Articles') cta = 'Discover →';
					else if(item.title === 'Gallery') cta = 'Browse →';
					return (
						<a
							key={item.title}
							href={item.link}
							className='group flex flex-col w-full aspect-square max-w-[380px] max-h-[380px] backdrop-blur-lg bg-black/70 border border-gray-800 shadow-xl overflow-hidden hover:scale-105 transition-transform duration-300 transform-gpu rounded items-center justify-center mx-auto'
						>
							<img
								src={item.image}
								alt={item.title}
								className='w-full h-[50%] min-h-[90px] max-h-[180px] object-cover group-hover:scale-105 transition-transform duration-300 rounded-t mx-auto'
							/>
							<div className='flex-1 flex flex-col p-6 items-center justify-center text-center'>
								<h2 className='text-2xl font-bold text-white mb-2 drop-shadow'>
									{item.title}
								</h2>
								<p className='text-gray-300 mb-2 flex-1 line-clamp-2'>{item.desc}</p>
								<span className='relative text-blue-400 font-semibold mt-2 after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-[2px] after:bg-blue-400 after:transition-all after:duration-300 group-hover:after:w-full transition'>{cta}</span>
							</div>
						</a>
					);
				})}
			</div>
		</section>
	);
}

// Animation variants for page transitions (fade only, slower for smoothness)
const pageVariants = {
	initial: { opacity: 0 },
	animate: { opacity: 1, transition: { duration: 0.85, ease: [0.4,0,0.2,1] } }
};

function AnimatedRoutes() {
	const location = useLocation();
	return (
		<AnimatePresence mode='wait'>
				<Routes location={location} key={location.pathname}>
					<Route path='/' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<div className='relative w-full h-[100vh]'>
								<img
									src='pictures/home_bg.JPG'
									alt='Mechanical Keyboard Banner'
									className='absolute inset-0 w-full h-full object-cover z-0 object-center'
									style={{objectPosition: 'center'}}
								/>
							</div>
							<div className='relative z-20 mt-12'>
								<FeaturedGrid />
							</div>
							<footer className='w-full flex flex-col sm:flex-row items-center sm:items-center justify-center sm:justify-between py-4 px-8 bg-black/80 border-t border-gray-800 shadow-2xl backdrop-blur-xl mt-8'>
								<div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8 w-full justify-center sm:justify-between text-center sm:text-left'>
									<span className='text-gray-200 text-sm sm:text-base font-medium'>
										AEYkbds is a keyboard repository for mechanical keyboard enthusiasts. Explore builds, soundtests, guides, and inspiration. Connect with us or follow for updates, collabs, and feedback:
									</span>
									<div className='flex flex-row gap-4 items-center flex-shrink-0 justify-center sm:justify-end w-full sm:w-auto'>
										<a href='mailto:aeykbds@gmail.com' className='relative text-blue-400 text-sm sm:text-base after:absolute after:left-0 after:right-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-blue-400 after:transition-all after:duration-300 hover:after:w-full transition px-0 mt-1'>Email</a>
										<a href='https://www.youtube.com/@aeykbds' target='_blank' rel='noopener noreferrer' className='relative text-[#FF0000] text-xl items-center gap-1 after:absolute after:left-0 after:right-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-[#FF0000] after:transition-all after:duration-300 hover:after:w-full transition px-0'>
											<i className='fab fa-youtube'></i><span className='text-sm sm:text-base'>YouTube</span>
										</a>
										<a href='https://www.tiktok.com/@aeykbds' target='_blank' rel='noopener noreferrer' className='relative text-[#25F4EE] text-xl items-center gap-1 after:absolute after:left-0 after:right-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-[#25F4EE] after:transition-all after:duration-300 hover:after:w-full transition px-0'>
											<i className='fab fa-tiktok'></i><span className='text-sm sm:text-base'>TikTok</span>
										</a>
										<a href='https://www.instagram.com/aeykbds' target='_blank' rel='noopener noreferrer' className='relative text-[#E1306C] text-xl items-center gap-1 after:absolute after:left-0 after:right-0 after:bottom-0 after:w-0 after:h-[2px] after:bg-[#E1306C] after:transition-all after:duration-300 hover:after:w-full transition px-0'>
											<i className='fab fa-instagram'></i><span className='text-sm sm:text-base'>Instagram</span>
										</a>
									</div>
								</div>
							</footer>
						</motion.div>
					} />
					<Route path='/gallery' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<GalleryPage />
							<ShortFooter />
						</motion.div>
					} />
					<Route path='/database' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<DatabasePage />
							<ShortFooter />
						</motion.div>
					} />
					<Route path='/articles' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<ArticlesPage />
							<ShortFooter />
						</motion.div>
					} />
					{/* Article dynamic route */}
					<Route path='/articles/:slug' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<ArticleRouter />
							<ShortFooter />
						</motion.div>
					} />
					<Route path='/in-progress' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<InProgress />
							<ShortFooter />
						</motion.div>
					} />
					<Route path='/soundtests' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<SoundtestsPage />
							<ShortFooter />
						</motion.div>
					} />
					<Route path='/requests' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<RequestsPage />
							<ShortFooter />
						</motion.div>
					} />
					<Route path='/switch-catalog' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<SwitchCatalogPage />
							<ShortFooter />
						</motion.div>
					} />
					<Route path='/build/:id' element={
						<motion.div initial='initial' animate='animate' exit='exit' variants={pageVariants} className='w-full min-h-screen'>
							<ShortFooter />
						</motion.div>
					} />
				</Routes>
		</AnimatePresence>
	);
}

function App() {
	return (
		<Router>
			<AudioProvider>
				<div className='bg-[#111112] relative'>
					<Navbar />
					<main className='flex flex-col items-center justify-center w-full mt-0'>
						<AnimatedRoutes />
					</main>
				</div>
			</AudioProvider>
		</Router>
	);
}

export default App;
